import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import Button from '@components/shared/Button';
import { useAppStore } from '@store/appStore';
import { saveSlideConfig, loadSlideConfig } from '@office/settingsService';
import { injectActivityShape, buildShapeDescriptor, getCurrentSlideIndex } from '@office/slideService';
import type { WordCloudConfig } from '@types-local/quiz.types';

export default function WordCloud(): React.ReactElement {
  const [prompt, setPrompt] = useState('');
  const [maxWords, setMaxWords] = useState(3);
  const [timeLimit, setTimeLimit] = useState(30);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [filterProfanity, setFilterProfanity] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isOfficeReady = useAppStore((s) => s.isOfficeReady);

  // Repopulate from the current slide's saved config on return.
  useEffect(() => {
    if (!isOfficeReady) return;
    let cancelled = false;
    (async () => {
      const cfg = loadSlideConfig(await getCurrentSlideIndex());
      if (cancelled || cfg?.type !== 'word_cloud') return;
      setPrompt(cfg.prompt);
      setMaxWords(cfg.maxWordsPerStudent);
      setTimeLimitEnabled(cfg.timeLimit !== null);
      if (cfg.timeLimit !== null) setTimeLimit(cfg.timeLimit);
      setFilterProfanity(cfg.filterProfanity);
    })();
    return () => { cancelled = true; };
  }, [isOfficeReady]);

  const handleSave = async () => {
    if (!prompt.trim()) return;
    setSaving(true);
    try {
      const config: WordCloudConfig = {
        type: 'word_cloud',
        prompt: prompt.trim(),
        maxWordsPerStudent: maxWords,
        timeLimit: timeLimitEnabled ? timeLimit : null,
        filterProfanity,
      };
      const slideIndex = await getCurrentSlideIndex();
      const shapeName = `IQ_QUIZ_WC_${slideIndex.toString().padStart(2, '0')}`;
      await saveSlideConfig(slideIndex, config, shapeName);
      await injectActivityShape(buildShapeDescriptor(config, shapeName), slideIndex);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-base">☁️</div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Word Cloud</h2>
          <p className="text-[10px] text-slate-500">Collect responses and display them as a cloud</p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Prompt</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Name one word that describes the Philippine Revolution…"
          rows={3}
          className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:border-orange-500 transition-colors"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          Max Words Per Student: <span className="text-orange-300">{maxWords}</span>
        </label>
        <input type="range" min={1} max={5} value={maxWords} onChange={(e) => setMaxWords(Number(e.target.value))}
          className="w-full accent-orange-500" />
        <div className="flex justify-between text-[10px] text-slate-400 mt-0.5"><span>1</span><span>5</span></div>
      </div>

      <div className="space-y-2.5">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Settings</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-700">⏱ Time Limit</span>
          <div className="flex items-center gap-2">
            {timeLimitEnabled && (
              <input type="number" min={5} max={300} value={timeLimit} onChange={(e) => setTimeLimit(Number(e.target.value))}
                className="w-14 bg-surface-elevated border border-surface-border rounded-lg px-2 py-1 text-xs text-slate-900 text-center focus:outline-none" />
            )}
            <button onClick={() => setTimeLimitEnabled((v) => !v)}
              className={`w-10 h-5 rounded-full transition-all ${timeLimitEnabled ? 'bg-orange-500' : 'bg-surface-elevated border border-surface-border'}`}>
              <div className={`w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform ${timeLimitEnabled ? 'translate-x-2.5' : '-translate-x-2.5'}`} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-700">🚫 Filter Profanity</span>
          <button onClick={() => setFilterProfanity((v) => !v)}
            className={`w-10 h-5 rounded-full transition-all ${filterProfanity ? 'bg-orange-500' : 'bg-surface-elevated border border-surface-border'}`}>
            <div className={`w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform ${filterProfanity ? 'translate-x-2.5' : '-translate-x-2.5'}`} />
          </button>
        </div>
      </div>

      <Button fullWidth onClick={handleSave} loading={saving}
        disabled={!prompt.trim() || !isOfficeReady}
        variant={saved ? 'secondary' : 'primary'}
        icon={saved ? <Check size={14} /> : undefined}
        className={saved ? '' : 'bg-orange-500 hover:bg-orange-600'}>
        {saved ? 'Saved to Slide!' : 'Add to Slide'}
      </Button>
    </div>
  );
}
